<template>
  <div class="page-card">
    <div class="page-head">
      <h3>{{ title }}</h3>
      <el-button type="primary" size="small" @click="openCreate">{{ createText }}</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input v-model="query.keyword" clearable placeholder="Username / name / email / phone" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="Status">
        <el-select v-model="query.status" clearable placeholder="All">
          <el-option label="Active" value="0" />
          <el-option label="Disabled" value="1" />
        </el-select>
      </el-form-item>
      <el-form-item v-if="isAppUserList" label="Coach">
        <el-select v-model="query.hasCoach" clearable placeholder="All">
          <el-option label="With Coach" value="yes" />
          <el-option label="No Coach" value="no" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="userId" label="ID" width="80" />
      <el-table-column prop="username" label="Username" min-width="150">
        <template slot-scope="scope">
          <span class="username-cell">
            <span>{{ scope.row.username }}</span>
            <el-tooltip v-if="isAppUserList && scope.row.hasCoach" content="Linked coach" placement="top">
              <i class="el-icon-star-on coach-icon"></i>
            </el-tooltip>
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="nickName" label="Name" min-width="130" />
      <el-table-column prop="email" label="Email" min-width="180" show-overflow-tooltip />
      <el-table-column v-if="isAppUserList" label="Coach" min-width="200" show-overflow-tooltip>
        <template slot-scope="scope">
          <span v-if="scope.row.coachUserId">
            {{ scope.row.coachName || scope.row.coachUserId }}
            <span v-if="scope.row.coachEmail" class="muted">({{ scope.row.coachEmail }})</span>
          </span>
          <span v-else class="muted">No Coach</span>
        </template>
      </el-table-column>
      <el-table-column prop="phone" label="Phone" min-width="130" />
      <el-table-column prop="status" label="Status" width="90">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === '0' ? 'success' : 'danger'" size="small">
            {{ scope.row.status === '0' ? 'Active' : 'Disabled' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="Created At" min-width="160" />
      <el-table-column label="Actions" width="170" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button
            type="text"
            size="small"
            class="danger"
            :disabled="protectBuiltInAdmin && scope.row.username === 'admin'"
            @click="remove(scope.row)"
          >Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="620px">
      <el-form :model="form" label-width="90px" size="small">
        <el-row :gutter="16">
          <el-col :span="12"><el-form-item label="Username"><el-input v-model="form.username" :disabled="!!form.userId" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Name"><el-input v-model="form.nickName" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Email"><el-input v-model="form.email" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Phone"><el-input v-model="form.phone" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="Status">
              <el-radio-group v-model="form.status">
                <el-radio label="0">Active</el-radio>
                <el-radio label="1">Disabled</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="Password">
          <el-input
            v-model="form.password"
            show-password
            :placeholder="form.userId ? 'Leave blank to keep current password' : 'Default: admin123'"
          />
        </el-form-item>
        <el-form-item v-if="showRoles" label="Roles">
          <el-select v-model="form.roleIds" multiple clearable style="width:100%" placeholder="Select roles">
            <el-option v-for="role in roleOptions" :key="role.roleId" :label="role.roleName" :value="role.roleId" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { adminUserApi, appUserApi, roleApi } from '@/api/system'

const emptyForm = clientType => ({
  userId: null,
  username: '',
  password: '',
  nickName: '',
  email: '',
  phone: '',
  status: '0',
  clientType,
  roleIds: []
})

const emptyQuery = clientType => ({ pageNum: 1, pageSize: 10, keyword: '', status: '', hasCoach: '', clientType })

export default {
  name: 'SysUserTable',
  props: {
    clientType: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    createText: {
      type: String,
      required: true
    },
    entityName: {
      type: String,
      required: true
    },
    showRoles: {
      type: Boolean,
      default: false
    },
    protectBuiltInAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(this.clientType),
      dialogVisible: false,
      dialogTitle: this.createText,
      form: emptyForm(this.clientType),
      roleOptions: []
    }
  },
  computed: {
    isAppUserList() {
      return this.clientType === 'app'
    }
  },
  created() {
    this.loadData()
    if (this.showRoles) this.loadRoles()
  },
  methods: {
    apiClient() {
      return this.clientType === 'admin' ? adminUserApi : appUserApi
    },
    async loadData() {
      this.loading = true
      try {
        const res = await this.apiClient().list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    async loadRoles() {
      const res = await roleApi.list({ pageNum: 1, pageSize: 100 })
      this.roleOptions = (res.data && res.data.records) || []
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery(this.clientType)
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = this.createText
      this.form = emptyForm(this.clientType)
      this.dialogVisible = true
    },
    async openEdit(row) {
      this.dialogTitle = `Edit ${this.entityName}`
      this.form = { ...emptyForm(this.clientType), ...row, clientType: this.clientType, password: '' }
      if (this.showRoles) {
        const res = await this.apiClient().roleIds(row.userId)
        this.form.roleIds = res.data || []
      }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.username) {
        this.$message.warning('Please enter a username')
        return
      }
      if (this.form.userId) await this.apiClient().update(this.cleanPayload(this.form))
      else await this.apiClient().create(this.cleanPayload(this.form))
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    cleanPayload(form) {
      const payload = { ...form, clientType: this.clientType }
      if (!payload.password) delete payload.password
      if (!this.showRoles) delete payload.roleIds
      return payload
    },
    async remove(row) {
      await this.$confirm(`Delete ${this.entityName} ${row.username}?`, 'Confirm', { type: 'warning' })
      await this.apiClient().remove(row.userId)
      this.$message.success('Deleted successfully')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
.muted { color: #909399; }
.username-cell { display: inline-flex; align-items: center; gap: 6px; }
.coach-icon {
  color: #6366f1;
  font-size: 16px;
  vertical-align: middle;
}
</style>
